/**
* @license Apache-2.0
*
* Copyright (c) 2025 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

/*
* When adding modules to the namespace, ensure that they are added in alphabetical order according to module name.
*/

// MODULES //

import setReadOnly from '@stdlib/utils/define-read-only-property';


// MAIN //

/**
* Top-level namespace.
*
* @namespace planck
*/
var planck = {};

/**
* @name cdf
* @memberof planck
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/planck/cdf}
*/
import cdf from './../../../../base/dists/planck/cdf';
setReadOnly( planck, 'cdf', cdf );

/**
* @name entropy
* @memberof planck
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/planck/entropy}
*/
import entropy from './../../../../base/dists/planck/entropy';
setReadOnly( planck, 'entropy', entropy );

/**
* @name kurtosis
* @memberof planck
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/planck/kurtosis}
*/
import kurtosis from './../../../../base/dists/planck/kurtosis';
setReadOnly( planck, 'kurtosis', kurtosis );

/**
* @name logcdf
* @memberof planck
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/planck/logcdf}
*/
import logcdf from './../../../../base/dists/planck/logcdf';
setReadOnly( planck, 'logcdf', logcdf );

/**
* @name logpmf
* @memberof planck
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/planck/logpmf}
*/
import logpmf from './../../../../base/dists/planck/logpmf';
setReadOnly( planck, 'logpmf', logpmf );

/**
* @name mean
* @memberof planck
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/planck/mean}
*/
import mean from './../../../../base/dists/planck/mean';
setReadOnly( planck, 'mean', mean );

/**
* @name median
* @memberof planck
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/planck/median}
*/
import median from './../../../../base/dists/planck/median';
setReadOnly( planck, 'median', median );

/**
* @name mgf
* @memberof planck
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/planck/mgf}
*/
import mgf from './../../../../base/dists/planck/mgf';
setReadOnly( planck, 'mgf', mgf );

/**
* @name mode
* @memberof planck
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/planck/mode}
*/
import mode from './../../../../base/dists/planck/mode';
setReadOnly( planck, 'mode', mode );

/**
* @name pmf
* @memberof planck
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/planck/pmf}
*/
import pmf from './../../../../base/dists/planck/pmf';
setReadOnly( planck, 'pmf', pmf );

/**
* @name quantile
* @memberof planck
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/planck/quantile}
*/
import quantile from './../../../../base/dists/planck/quantile';
setReadOnly( planck, 'quantile', quantile );

/**
* @name skewness
* @memberof planck
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/planck/skewness}
*/
import skewness from './../../../../base/dists/planck/skewness';
setReadOnly( planck, 'skewness', skewness );

/**
* @name stdev
* @memberof planck
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/planck/stdev}
*/
import stdev from './../../../../base/dists/planck/stdev';
setReadOnly( planck, 'stdev', stdev );

/**
* @name variance
* @memberof planck
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/planck/variance}
*/
import variance from './../../../../base/dists/planck/variance';
setReadOnly( planck, 'variance', variance );


// EXPORTS //

export default planck;
